import { motion } from "framer-motion";
import { Target, Eye, Users, Award, Globe, TrendingUp } from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To provide seamless, reliable, and innovative logistics solutions that empower businesses to thrive in the global marketplace."
    },
    {
      icon: Eye,
      title: "Our Vision",
      description: "To be the world's most trusted logistics partner, setting the standard for excellence in delivery and customer service."
    },
    {
      icon: Users,
      title: "Our Team",
      description: "A dedicated team of logistics experts, drivers, and support staff committed to delivering your cargo safely and on time."
    }
  ];

  const milestones = [
    { year: "2010", event: "Company Founded", desc: "Started with a single warehouse and 5 trucks" },
    { year: "2014", event: "National Expansion", desc: "Expanded operations to all 50 states" },
    { year: "2017", event: "International Launch", desc: "Opened first international shipping routes" },
    { year: "2020", event: "Digital Transformation", desc: "Launched real-time tracking platform" },
    { year: "2024", event: "Global Network", desc: "Now serving 120+ countries worldwide" }
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1576888841486-afe7f1b2d74a?w=1920&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-wider">About Us</span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-4 mb-6 tracking-tight uppercase"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Moving the World,<br />One Shipment at a Time
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Since 2010, SwiftLogix has been at the forefront of logistics innovation, connecting businesses with global markets through our comprehensive shipping and delivery solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">Our Story</span>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6 tracking-tight"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Built on Trust, Driven by Excellence
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                What started as a small local delivery service has grown into a global logistics powerhouse. Our journey has been marked by continuous innovation, unwavering commitment to customer satisfaction, and a relentless pursuit of operational excellence.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Today, SwiftLogix operates a fleet of over 500 vehicles, serves customers in more than 120 countries, and processes over 50,000 shipments monthly. Our success is built on the simple principle of treating every package as if it were our own.
              </p>

              <div className="grid grid-cols-3 gap-6 mt-8">
                <div className="text-center p-4 bg-slate-50 rounded-sm">
                  <Globe className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">120+</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Countries</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-sm">
                  <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">50K+</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Monthly</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-sm">
                  <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">14+</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Years</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1766793110924-98e05b48eadc?w=800&q=80"
                alt="SwiftLogix warehouse"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Team */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">Our Foundation</span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((item, index) => (
              <motion.div
                key={item.title}
                className="bg-white p-8 rounded-sm border border-slate-200 shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 bg-slate-900 rounded-sm flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">Our Journey</span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Key Milestones
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-slate-200 hidden md:block"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-slate-50 p-6 rounded-sm inline-block">
                      <div className="text-3xl font-bold text-blue-600 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                        {milestone.year}
                      </div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-1">
                        {milestone.event}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {milestone.desc}
                      </p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-md z-10 hidden md:block"></div>
                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
